/**
 * i18n runtime configuration (I18N-T1).
 *
 * i18next + react-i18next + http-backend. Locale JSONs are lazy-loaded from
 * public/locales/{en,hi}/<namespace>.json. English is the source of truth and the
 * automatic fallback — a key missing in Hindi renders English, never the raw key.
 *
 * Only English + Hindi are supported at Tier 1 (the switcher offers only these two —
 * we never promise a language we haven't translated). The initial language is the
 * fast-path localStorage value; AuthContext then corrects it per role on login/boot
 * (see applyLanguageForRole in ./index — the leak guard that keeps staff portals English).
 *
 * @module i18n/config
 */
import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

export const STORAGE_KEY = 'aisistoo_language';
export type SupportedLang = 'en' | 'hi';

/** localStorage fast-path, validated to a supported language. */
function initialLang(): SupportedLang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'hi' ? 'hi' : 'en';
  } catch {
    return 'en';
  }
}

void i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: initialLang(),
    fallbackLng: 'en',
    supportedLngs: ['en', 'hi'],
    load: 'currentOnly',
    // Existing foundation scaffold namespaces in Tier-1 scope. Login strings live under
    // common.auth.* (there is no separate `auth` namespace). admin/owner/staff/teacher
    // namespaces exist on disk but are out of scope and are not loaded.
    ns: ['common', 'student'],
    defaultNS: 'common',
    backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
    interpolation: { escapeValue: false }, // React already escapes
    returnNull: false, // missing → '' then fallback, never null
    react: { useSuspense: true },
  });

export default i18n;
